/**
 * Flywheel kit frontend — the reusable CQRS base.
 *
 * Resources are generated from the kit's descriptors (kit-resources), reads/writes are
 * split by the composite `default` provider (dataProviders), and every entity renders
 * through the generic descriptor-driven pages. A new kit swaps its descriptors and gets
 * a working portal — no per-entity page code.
 */

import React from "react";
import { Refine, Authenticated } from "@refinedev/core";
import { useNotificationProvider, ErrorComponent } from "@refinedev/antd";
import routerProvider, {
  NavigateToResource,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { ConfigProvider, App as AntdApp, Typography } from "antd";
import itIT from "antd/locale/it_IT";
import {
  authProvider,
  CRMLayout,
  HubLayout,
  type HubNavGroup,
  initFlywheelRegistry,
  setPortalChrome,
  descriptorTextTransformers,
  italianI18nProvider,
  useWidePanel,
} from "@evo-flywheel/ui";
import { descriptors } from "@kit/descriptors/index";
import { coreBOSMap } from "@kit/corebos-map";

import { dataProvider } from "./dataProviders";
import { refineResources, kitResources } from "./kit-resources";
import { instance } from "./instance";
import { LoginPage } from "./pages/login";
import { buildNav, SPAZI_CHANGED } from "./nav";
import { mosaico, type SpazioInfo } from "./knowledge/mosaico/api";
// Related column (rule 13) and module slots (rule 14) register at boot.
import "./modules";
import { GenericList } from "./pages/generic/GenericList";
import { GenericShow } from "./pages/generic/GenericShow";
import { GenericCreate } from "./pages/generic/GenericCreate";
import { GenericEdit } from "./pages/generic/GenericEdit";
/** A tab opened before a deploy asks for chunk names that no longer exist; reload once onto the
 * current build instead of unmounting the whole app (blank page). */
const lazyPage = <T,>(load: () => Promise<T>) => () =>
  load().then((m) => { sessionStorage.removeItem("chunk-reload"); return m; }, (e) => {
    if (!sessionStorage.getItem("chunk-reload")) { sessionStorage.setItem("chunk-reload", "1"); window.location.reload(); }
    throw e;
  });

// Lazy: the editor (TipTap) and WebGL graph (sigma) load only when il Mosaico is opened.
const KnowledgeWorkspace = React.lazy(lazyPage(() =>
  import("./knowledge/KnowledgeWorkspace").then((m) => ({ default: m.KnowledgeWorkspace }))),
);
const MappaPage = React.lazy(lazyPage(() => import("./knowledge/KnowledgeWorkspace").then((m) => ({ default: m.MappaPage }))));
const AtomiPage = React.lazy(lazyPage(() => import("./knowledge/KnowledgeWorkspace").then((m) => ({ default: m.AtomiPage }))));
// ADR-047: project labels and the project view.
const ProgettiList = React.lazy(lazyPage(() => import("./progetti/ProgettiPage").then((m) => ({ default: m.ProgettiList }))));
const ProgettoShow = React.lazy(lazyPage(() => import("./progetti/ProgettiPage").then((m) => ({ default: m.ProgettoShow }))));
const DocumentsPage = React.lazy(lazyPage(() => import("./documents/DocumentsPage").then((m) => ({ default: m.DocumentsPage }))));

// ADR-021: per-user connector drivers (Gmail, Calendar, Drive).
const ConnectionsPage = React.lazy(lazyPage(() =>
  import("./connections/ConnectionsPage").then((m) => ({ default: m.ConnectionsPage }))),
);
const ConnectionsButton = React.lazy(lazyPage(() =>
  import("./connections/ConnectionsPage").then((m) => ({ default: m.ConnectionsButton }))),
);

// ADR-041: execution evidence (operators and admin staff until kernel 0.4.0 groups).
const EvidencePage = React.lazy(lazyPage(() =>
  import("./evidence/EvidencePage").then((m) => ({ default: m.EvidencePage }))),
);
const EvidenceButton = React.lazy(lazyPage(() =>
  import("./evidence/EvidencePage").then((m) => ({ default: m.EvidenceButton }))),
);

const isPortal = instance.tier === "portal";

// The active instance profile (ADR-001 §8) may trim the module set.
const enabledResources =
  instance.modules && instance.modules.length > 0
    ? kitResources.filter((r) => instance.modules!.includes(r.name))
    : kitResources;
const enabledNames = new Set(enabledResources.map((r) => r.name));
const activeResources = refineResources.filter((r) => enabledNames.has(r.name));
const firstResource = enabledResources[0]?.name ?? "";

// Boot the descriptor-driven registry so the shell (nav groups, ⌘K, panels)
// resolves modules from the kit's descriptors — no static manifests.
// Il Mosaico (ADR-045) is a module of its own in the menu, served by the kit's pack routes rather
// than a descriptor. The coreBOS "Voci Wiki" catalogue leaves the menu: /wiki_entry now redirects.
const mosaicoModule = {
  slug: "mosaico", kind: "document", label: { singular: "Tessera", plural: "Il Mosaico" }, fields: [],
  table: "mosaico_tessera", idStrategy: "sha256",
  ui: { mode: "generated", icon: "AppstoreOutlined", navGroup: "Conoscenza", navOrder: 0 },
} as unknown as (typeof descriptors)[number];
const toolModule = (slug:string, plural:string, icon:string, order:number) => ({ slug, kind:"document", label:{singular:plural,plural}, fields:[], table:`ui_${slug}`, idStrategy:"sha256", ui:{mode:"generated",icon,navGroup:"Conoscenza",navOrder:order} }) as unknown as (typeof descriptors)[number];
const navDescriptors = [
  mosaicoModule,
  toolModule("documenti","Archivio documenti","FolderOutlined",1),
  toolModule("mappa","Mappa del Mosaico","ApartmentOutlined",2),
  toolModule("atomi","Atomi","ExperimentOutlined",3),
  toolModule("progetti","Progetti","ProjectOutlined",4),
  ...descriptors.map((d) => (d.slug === "wiki_entry" ? { ...d, ui: { ...(d.ui as object), hidden: true } } as typeof d : d)),
];
initFlywheelRegistry({ descriptors: navDescriptors, corebosMap: coreBOSMap });

/** Old links (faber, bookmarks) keep working: /wiki_entry?apri=… → /mosaico?apri=… */
const ToMosaico: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/mosaico${search}`} replace />;
};
/** Permalink (ADR-045 amendment 2026-10-31): /mosaico/t/<id> opens the tessera whatever its title is now. */
const PermalinkTessera: React.FC = () => {
  const { pathname } = useLocation();
  const id = decodeURIComponent(pathname.split("/").pop() ?? "");
  const [to, setTo] = React.useState<string | null>(null);
  React.useEffect(() => {
    void mosaico.perId(id).then((t) => setTo(`/mosaico?apri=${encodeURIComponent(`${t.spazio}/${t.titolo}`)}`)).catch(() => setTo("/mosaico?inaccessibile=1"));
  }, [id]);
  return to ? <Navigate to={to} replace /> : null;
};
const MappaInstrument: React.FC = () => {
  const { setWidePanelContent, setPanelState } = useWidePanel();
  React.useEffect(() => {
    setWidePanelContent(<React.Suspense fallback={null}><MappaPage /></React.Suspense>);
    setPanelState("full");
    return () => { setWidePanelContent(null); setPanelState("collapsed"); };
  }, [setWidePanelContent, setPanelState]);
  return <div style={{padding:32}}><Typography.Title level={3}>Mappa del Mosaico</Typography.Title><Typography.Paragraph>La mappa è aperta nello strumento verticale. Riducilo per tornare alla lettura normale.</Typography.Paragraph></div>;
};

// UI contract rule 11: titles come from the descriptor labels (no English inflector), and the
// stock Refine text (buttons, notifications) is Italian.
const textTransformers = descriptorTextTransformers(descriptors);
const i18nProvider = italianI18nProvider({}, {
  resourceLabel: (slug) => descriptors.find((d) => d.slug === slug)?.label.singular,
});

// Portal tier: brand the shell chrome from the instance profile. CRM entities
// remain real modules (customer/supplier access); this only re-skins the
// assistant copy. The hub tier has no assistant, so this is skipped.
if (isPortal && instance.assistant) {
  setPortalChrome({
    brand: { logoSrc: instance.logoSrc ?? "", logoAlt: instance.brand },
    assistant: instance.assistant,
  });
}

// Hub tier: data-driven nav from the kit's resources (one ungrouped run).
const hubNav: HubNavGroup[] = [
  {
    key: "modules",
    items: enabledResources.map((r) => ({
      key: r.name,
      label: r.meta.label,
      path: `/${r.name}`,
    })),
  },
  { key: "conoscenza", items: [
    { key: "mosaico", label: "Il Mosaico", path: "/mosaico" }, { key: "documenti", label: "Archivio documenti", path: "/documenti" },
    { key: "mappa", label: "Mappa", path: "/mappa" }, { key: "atomi", label: "Atomi", path: "/atomi" },
    { key: "progetti", label: "Progetti", path: "/progetti" },
  ] },
  // ADR-041: the page itself answers 403 for anyone who is not AI governance.
  { key: "evidence", items: [{ key: "evidence", label: "Evidenze", path: "/evidence" }] },
];

// DEV-only: `?preview` renders the portal shell without the auth gate, so the
// layout is visible without a live backend. Stripped from production builds.
const previewMode =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).has("preview");

// The shell element wraps the routed Outlet. Portal → CRMLayout behind the auth
// gate (or open in preview); hub → the kernel-free HubLayout, no auth gate.
/** Portal shell: the grouped left rail (UI contract) with the reader's spazi as its first group. */
const PortalShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [spazi, setSpazi] = React.useState<SpazioInfo[]>([]);
  React.useEffect(() => {
    const load = () => void mosaico.spazi().then(setSpazi).catch(() => setSpazi([]));
    load();
    window.addEventListener(SPAZI_CHANGED, load);
    return () => window.removeEventListener(SPAZI_CHANGED, load);
  }, []);
  const nav = React.useMemo(() => buildNav(enabledResources, spazi), [spazi]);
  return <CRMLayout nav={nav} navMode="groups">{children}</CRMLayout>;
};

const shellElement = isPortal ? (
  previewMode ? (
    <PortalShell>
      <Outlet />
    </PortalShell>
  ) : (
    <Authenticated key="protected" fallback={<LoginPage />}>
      <PortalShell>
        <Outlet />
        <React.Suspense fallback={null}><ConnectionsButton /></React.Suspense>
        <React.Suspense fallback={null}><EvidenceButton /></React.Suspense>
      </PortalShell>
    </Authenticated>
  )
) : (
  <HubLayout brand={instance.brand} nav={hubNav}>
    <Outlet />
  </HubLayout>
);

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ConfigProvider locale={itIT}>
        <AntdApp>
          <Refine
            routerProvider={routerProvider}
            dataProvider={dataProvider}
            authProvider={authProvider}
            notificationProvider={useNotificationProvider}
            i18nProvider={i18nProvider}
            resources={activeResources}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              disableTelemetry: true,
              textTransformers,
            }}
          >
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route element={shellElement}>
                <Route
                  index
                  element={<NavigateToResource resource={firstResource} />}
                />

                {enabledResources.map((r) =>
                  // ADR-018: the wiki is a kernel-owned knowledge workspace, not a generic list.
                  r.name === "wiki_entry" ? (
                    <Route key={r.name} path="/wiki_entry/*" element={<ToMosaico />} />
                  ) : (
                  <Route key={r.name} path={`/${r.name}`}>
                    <Route index element={<GenericList />} />
                    <Route path="create" element={<GenericCreate />} />
                    <Route path="edit/:id" element={<GenericEdit />} />
                    <Route path="show/:id" element={<GenericShow />} />
                  </Route>
                  ),
                )}

                {isPortal && (
                  <Route path="/connections" element={<React.Suspense fallback={null}><ConnectionsPage /></React.Suspense>} />
                )}
                <Route path="/mosaico/mappa" element={<Navigate to="/mappa" replace />} />
                <Route path="/mosaico/atomi" element={<Navigate to="/atomi" replace />} />
                <Route path="/mosaico/t/:id" element={<PermalinkTessera />} />
                <Route path="/mosaico/*" element={<React.Suspense fallback={null}><KnowledgeWorkspace /></React.Suspense>} />
                <Route path="/documenti" element={<React.Suspense fallback={null}><DocumentsPage /></React.Suspense>} />
                <Route path="/mappa" element={<MappaInstrument />} />
                <Route path="/atomi" element={<div style={{padding:16}}><React.Suspense fallback={null}><AtomiPage /></React.Suspense></div>} />
                <Route path="/progetti" element={<React.Suspense fallback={null}><ProgettiList /></React.Suspense>} />
                <Route path="/progetti/:id" element={<React.Suspense fallback={null}><ProgettoShow /></React.Suspense>} />
                <Route path="/evidence" element={<React.Suspense fallback={null}><EvidencePage /></React.Suspense>} />

                <Route path="*" element={<ErrorComponent />} />
              </Route>
            </Routes>

            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
};

export default App;
